import {
  Backpack,
  Bandage,
  BatteryCharging,
  Binoculars,
  Bug,
  CloudRain,
  Droplets,
  Flashlight,
  Footprints,
  Glasses,
  Hand,
  Moon,
  Mountain,
  Package,
  Plug,
  Shirt,
  ShoppingBag,
  Snowflake,
  Sun,
  Thermometer,
  Wind,
} from "lucide-react";
import { dictionaries } from "../lib/i18n";
import { packingSearchUrl } from "../utils/packingLink";
import type { PackedItem, PackingAdvice, PackingItem, UiLang } from "../lib/types";

interface Props {
  packing: PackingAdvice;
  lang: UiLang;
}

/**
 * Klesrådet, på skjermen — ikke bare på papiret.
 *
 * Rådet fantes allerede: hele beregningen i services/climate.ts, hver eneste
 * gjenstand. Det som ikke fantes var et sted å se det før man skulle skrive ut
 * en roadbook — planen kunne stå ferdig på skjermen i ti minutter uten at
 * brukeren noen gang fikk vite at Sognefjellet i februar betyr vinterjakke og
 * brodder. `RoadBook.tsx` hadde panelet for seg selv, og de fleste åpner ikke
 * utskriftsversjonen før de faktisk reiser.
 *
 * ── To bolker, ikke én lang liste ──────────────────────────────────────────
 *
 * «Klær» og «annet» er brukerens eget spørsmål, ikke en oppfunnet inndeling:
 * man tenker «hva har jeg på meg» og «hva har jeg med» som to atskilte
 * pakkeoppgaver. `PACKING_CATEGORY` i services/messages.ts avgjør hvilken
 * bolk hver gjenstand havner i, så inndelingen følger nøkkelen og ikke en
 * gjetning på den oversatte teksten — det holder for alle fem språk.
 *
 * ── Hvorfor hver ting har en søkelenke ──────────────────────────────────────
 *
 * Rådet sier «brodder». Det svarer ikke på det neste spørsmålet en tilreisende
 * uten brodder faktisk stiller: hvor får jeg tak i dette? Lenka er et ekte søk
 * — «kjøpe brodder» — ikke en påstått anbefaling til en bestemt butikk. Se
 * utils/packingLink.ts for hvorfor et butikkspesifikt søk ble forkastet:
 * samme regel som resten av appen følger for lenker den ikke kan sjekke er
 * ekte, se utils/placeLink.ts.
 */

const ICON: Record<PackingItem, typeof Shirt> = {
  woolBase: Shirt,
  winterJacket: Shirt,
  downJacket: Shirt,
  midLayer: Shirt,
  hatGloves: Hand,
  layers: Shirt,
  rainShell: CloudRain,
  rainTrousers: CloudRain,
  windShell: Wind,
  sturdyBoots: Footprints,
  iceGrips: Snowflake,
  shorts: Sun,
  tshirt: Shirt,
  swimwear: Thermometer,
  sunscreen: Sun,
  sunglasses: Glasses,
  sleepMask: Moon,
  insectRepellent: Bug,
  headlamp: Flashlight,
  firstAidKit: Bandage,
  hikingPoles: Mountain,
  powerBank: BatteryCharging,
  dryBag: Package,
  binoculars: Binoculars,
  plugAdapter: Plug,
  reusableBottle: Droplets,
};

function Group({
  title,
  items,
  lang,
}: {
  title: string;
  items: PackedItem[];
  lang: UiLang;
}) {
  const t = dictionaries[lang];
  if (items.length === 0) return null;

  return (
    <div className="packing-group">
      <h4 className="packing-group-title">{title}</h4>
      <ul className="packing-list">
        {items.map((item) => {
          const Icon = ICON[item.key];
          return (
            <li className="packing-item" key={item.key}>
              <span className="packing-icon">
                <Icon size={14} />
              </span>
              <span className="packing-label">{item.label}</span>
              <a
                className="packing-shop no-print"
                href={packingSearchUrl(item.label, lang)}
                target="_blank"
                rel="noreferrer"
                aria-label={t.packingShopFor(item.label)}
                title={t.packingShop}
              >
                <ShoppingBag size={12} />
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function PackingPanel({ packing, lang }: Props) {
  const t = dictionaries[lang];
  const clothing = packing.items.filter((item) => item.category === "clothing");
  const gear = packing.items.filter((item) => item.category === "gear");

  return (
    <div className="card packing">
      <div className="card-title">
        <Backpack size={14} style={{ verticalAlign: -2, marginRight: 7 }} />
        {t.packingTitle}
      </div>
      <p className="card-sub">{packing.headline}</p>

      <Group title={t.packingClothing} items={clothing} lang={lang} />
      <Group title={t.packingGear} items={gear} lang={lang} />

      <div className="packing-foot">
        <p className="packing-note">
          {t.packingWetNote(Math.round(packing.wetShare * 100))}
          {packing.snow && ` ${t.packingSnowNote}`}
        </p>
        {/*
          Samme ærlighet som lyttetipsene og videolenkene: appen sier at
          knappene er søk, ikke at den vet hvor man bør handle.
        */}
        <p className="packing-shop-note no-print">
          <ShoppingBag size={11} /> {t.packingShopHint}
        </p>
      </div>
    </div>
  );
}
